import React, { createContext, useContext, useState, useEffect } from "react";
import { useIsFetching, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns-tz";
import {
  producaoService,
  RankingFilters,
  UsuarioRanking,
  UsuarioProducao,
} from "../../../../services/producaoService";
import { usuarioService } from "../../../../services/usuarioService";
import { lojaService } from "../../../../services/lojaService";
import { BrazilDate } from "../../../../utils/BrazilDate";
import { useRankingEvents } from "../../../../hooks/useRankingEvents";
import { RANKING_SSE_QUERY_KEYS } from "../../../../constants/rankingEventQueryKeys";

interface EstatisticasProducaoContextValue {
  ranking: UsuarioRanking[];
  usuariosProducao: UsuarioProducao[];
  isLoading: boolean;
  isFetchingProducao: boolean;
  getCorPorIndice: (index: number) => string;
  formatarTempo: (tempo: string) => string;
  appliedFilters: RankingFilters;
  applyFilters: (filters: RankingFilters) => void;
  startDate: string;
  endDate: string;
  selectedLojaId: number | null;
  selectedUsuarioId: number | null;
  setStartDate: (date: string) => void;
  setEndDate: (date: string) => void;
  setSelectedLojaId: (id: number | null) => void;
  setSelectedUsuarioId: (id: number | null) => void;
  handleApplyFilter: () => void;
  lojaOptions: Array<{ value: number; label: string }>;
  usuarioOptions: Array<{ value: number; label: string }>;
}

interface EstatisticasProducaoProviderProps {
  children: React.ReactNode;
}

const EstatisticasProducaoContext =
  createContext<EstatisticasProducaoContextValue>(
    {} as EstatisticasProducaoContextValue
  );

export const EstatisticasProducaoProvider: React.FC<
  EstatisticasProducaoProviderProps
> = ({ children }) => {
  const queryClient = useQueryClient();

  const isFetchingProducao =
    useIsFetching({
      predicate: (query) => {
        const key = query.queryKey;
        if (!Array.isArray(key) || key.length === 0) return false;
        const first = key[0] as unknown;
        return (
          first === "producao-resumo" ||
          first === "ranking" ||
          first === "usuarios-producao" ||
          first === "tamanhos" ||
          first === "bordas"
        );
      },
    }) > 0;

  const getDefaultDates = () => {
    const now = new Date();
    const timezone = BrazilDate.getTimezone();
    const yearStr = format(now, "yyyy", { timeZone: timezone });
    const monthStr = format(now, "MM", { timeZone: timezone });
    const year = parseInt(yearStr, 10);
    const month = parseInt(monthStr, 10);

    const firstDay = BrazilDate.create(year, month, 1);

    const nextMonth = month === 12 ? 1 : month + 1;
    const nextYear = month === 12 ? year + 1 : year;
    const firstDayNextMonth = BrazilDate.create(nextYear, nextMonth, 1);

    const lastDay = new Date(firstDayNextMonth.getTime() - 24 * 60 * 60 * 1000);

    return {
      dataInicio: BrazilDate.formatISO(firstDay),
      dataFim: BrazilDate.formatISO(lastDay),
    };
  };

  const [appliedFilters, setAppliedFilters] = useState<RankingFilters>({
    ...getDefaultDates(),
    lojaId: null,
    usuarioId: null,
  });

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedLojaId, setSelectedLojaId] = useState<number | null>(null);
  const [selectedUsuarioId, setSelectedUsuarioId] = useState<number | null>(
    null
  );

  const { data: lojas = [] } = useQuery({
    queryKey: ["lojas"],
    queryFn: () => lojaService.getLojas(),
  });

  const { data: usuariosResponse } = useQuery({
    queryKey: ["usuarios", "options"],
    queryFn: () => usuarioService.getUsuarios({ limit: 1000 }),
  });
  const usuariosLista = usuariosResponse?.data ?? [];

  const { data: rankingData, isLoading } = useQuery({
    queryKey: ["ranking", appliedFilters],
    queryFn: () => producaoService.getRanking(appliedFilters),
    enabled: !!(appliedFilters.dataInicio && appliedFilters.dataFim),
  });

  const { data: usuariosData } = useQuery({
    queryKey: ["usuarios-producao", appliedFilters],
    queryFn: () =>
      producaoService.getUsuarios({
        dataInicio: appliedFilters.dataInicio,
        dataFim: appliedFilters.dataFim,
        lojaId: appliedFilters.lojaId,
      }),
    enabled: !!(appliedFilters.dataInicio && appliedFilters.dataFim),
  });

  useRankingEvents("producao", appliedFilters.lojaId, {
    queryKeys: RANKING_SSE_QUERY_KEYS.estatisticasProducao,
  });

  useEffect(() => {
    setStartDate(appliedFilters.dataInicio);
    setEndDate(appliedFilters.dataFim);
    setSelectedLojaId(appliedFilters.lojaId || null);
    setSelectedUsuarioId(appliedFilters.usuarioId || null);
  }, [appliedFilters]);

  const lojaOptions = lojas.map((loja) => ({
    value: loja.id,
    label: loja.nome,
  }));

  const usuarioOptions = usuariosLista.map((usuario) => ({
    value: usuario.id,
    label: usuario.nome,
  }));

  const cores = [
    "bg-red-500",
    "bg-blue-500",
    "bg-green-500",
    "bg-purple-500",
    "bg-pink-500",
    "bg-indigo-500",
    "bg-yellow-500",
    "bg-teal-500",
    "bg-orange-500",
    "bg-cyan-500",
    "bg-lime-500",
    "bg-emerald-500",
    "bg-violet-500",
    "bg-fuchsia-500",
    "bg-rose-500",
  ];

  const getCorPorIndice = (index: number) => {
    return cores[index % cores.length];
  };

  const formatarTempo = (tempo: string) => {
    const [horas, minutos, segundos] = tempo.split(":").map(Number);

    if (horas > 0) {
      return `${horas}h ${minutos}m ${segundos}s`;
    } else if (minutos > 0) {
      return `${minutos}m ${segundos}s`;
    } else {
      return `${segundos}s`;
    }
  };

  const applyFilters = (filters: RankingFilters) => {
    setAppliedFilters(filters);
  };

  const handleApplyFilter = () => {
    const newFilters: RankingFilters = {
      dataInicio: startDate,
      dataFim: endDate,
      lojaId: selectedLojaId,
      usuarioId: selectedUsuarioId,
    };

    applyFilters(newFilters);

    queryClient.invalidateQueries({
      queryKey: ["producao-resumo", newFilters],
    });
    queryClient.invalidateQueries({ queryKey: ["ranking", newFilters] });
    queryClient.invalidateQueries({
      queryKey: ["usuarios-producao", newFilters],
    });
    queryClient.invalidateQueries({ queryKey: ["tamanhos", newFilters] });
    queryClient.invalidateQueries({ queryKey: ["bordas", newFilters] });
  };

  const ranking = rankingData?.ranking || [];
  const usuariosProducao = usuariosData?.usuarios || [];

  return (
    <EstatisticasProducaoContext.Provider
      value={{
        ranking,
        usuariosProducao,
        isLoading,
        isFetchingProducao,
        getCorPorIndice,
        formatarTempo,
        appliedFilters,
        applyFilters,
        startDate,
        endDate,
        selectedLojaId,
        selectedUsuarioId,
        setStartDate,
        setEndDate,
        setSelectedLojaId,
        setSelectedUsuarioId,
        handleApplyFilter,
        lojaOptions,
        usuarioOptions,
      }}
    >
      {children}
    </EstatisticasProducaoContext.Provider>
  );
};

export const useEstatisticasProducao = () => {
  const context = useContext(EstatisticasProducaoContext);
  if (!context) {
    throw new Error(
      "useEstatisticasProducao must be used within EstatisticasProducaoProvider"
    );
  }
  return context;
};

export const useEstatisticasProducaoFilters = () => {
  const { appliedFilters, applyFilters } = useEstatisticasProducao();
  return { appliedFilters, applyFilters };
};
